/* Parans 组合横幅 — 轮播控制器
 * 第 1 屏 = 后台第一个整图幻灯片,第 2 屏 = 图层画布,第 3 屏起 = 其余整图。
 * 功能:自动播放 + 进度条指示器、左右箭头、鼠标拖拽 / 触屏滑动、点击指示条切换、无限循环、
 *       背景色随当前屏切换、prefers-reduced-motion 关自动播放、主题编辑器区块联动。
 */
(function () {
  'use strict';

  var SLIDE_ANIM = 'transform 0.5s cubic-bezier(0.22, 0.61, 0.36, 1)';

  function ParansBanner(root) {
    this.root = root;
    this.viewport = root.querySelector('.parans-banner__viewport');
    this.track = root.querySelector('.parans-banner__track');
    if (!this.viewport || !this.track) return;

    this.slides = Array.prototype.slice.call(
      this.track.querySelectorAll('.parans-banner__slide')
    );
    /* 链接与图片在桌面浏览器中默认可被原生拖动，会先触发 pointercancel，
       导致第 2/3 张整图 Banner 的轮播拖拽偶发失效。 */
    Array.prototype.forEach.call(
      this.viewport.querySelectorAll('a, img'),
      function (el) { el.setAttribute('draggable', 'false'); }
    );
    this.n = this.slides.length;
    this.dots = Array.prototype.slice.call(
      root.querySelectorAll('.parans-banner__dot')
    );
    this.prevBtn = root.querySelector('.parans-banner__arrow--prev');
    this.nextBtn = root.querySelector('.parans-banner__arrow--next');

    this.reduceMotion =
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay =
      root.getAttribute('data-autoplay') === 'true' && !this.reduceMotion;
    this.interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    this.loop = root.getAttribute('data-loop') !== 'false';

    this.index = 0;
    this.paused = false;
    this.rafId = null;
    this.elapsed = 0;
    this.lastTs = 0;

    // 拖拽状态
    this.dragging = false;
    this.moved = false;
    this.suppressClick = false;   // 只吞"紧跟拖拽的合成 click",每次新按下清除
    this.captured = false;        // 是否已捕获指针(只有真正拖动才捕获,纯点击不捕获→click 能到达链接)
    this.startX = 0;
    this.startY = 0;
    this.deltaX = 0;
    this.baseShift = 0;
    this.pointerId = null;
    this.dragAxis = '';

    // 背景缓存(第 1 屏原始背景)
    this.origBg = root.style.getPropertyValue('--parans-banner-bg');
    this.origBgImg = root.style.getPropertyValue('--parans-banner-bg-img');

    this.bind();
    this.go(0, false);

    root.__paransCarousel = this;
  }

  ParansBanner.prototype.vw = function () {
    /* 0720修复:桌面等比缩放(main 有 zoom)下必须用未缩放的 offsetWidth——
       getBoundingClientRect 返回"缩放后"宽度,而 translateX 在未缩放坐标系执行、
       再整体被 zoom 放大 → 每切一屏偏移 (zoom-1)×屏宽(1920 屏≈430px):
       第1屏(位移0)永远正常,第2/3屏右侧漏出下一屏 —— 前台与编辑器(近乎无缩放)表现不一致的根因。 */
    return this.viewport.offsetWidth || this.viewport.getBoundingClientRect().width;
  };

  /* 视觉像素 ↔ 布局像素 的换算系数(zoom 环境 >1;无缩放=1)。拖拽的 clientX 是视觉像素,需除以它。 */
  ParansBanner.prototype.zoomFactor = function () {
    var ow = this.viewport.offsetWidth;
    return ow ? this.viewport.getBoundingClientRect().width / ow : 1;
  };

  ParansBanner.prototype.setTransform = function (px, animate) {
    this.track.style.transition = animate ? SLIDE_ANIM : 'none';
    this.track.style.transform = 'translate3d(' + px + 'px, 0, 0)';
  };

  ParansBanner.prototype.go = function (i, animate) {
    if (this.n <= 1) {
      this.syncBg();
      return;
    }
    if (this.loop) {
      i = ((i % this.n) + this.n) % this.n;
    } else {
      i = Math.max(0, Math.min(this.n - 1, i));
    }
    this.index = i;
    this.setTransform(-this.index * this.vw(), animate !== false);
    this.updateDots();
    this.syncBg();
    this.restartProgress();
  };

  ParansBanner.prototype.updateDots = function () {
    var self = this;
    this.dots.forEach(function (d, di) {
      var active = di === self.index;
      d.classList.toggle('is-active', active);
      d.setAttribute('aria-current', active ? 'true' : 'false');
    });
  };

  ParansBanner.prototype.syncBg = function () {
    var slide = this.slides[this.index];
    var bg = slide && slide.getAttribute('data-slide-bg');
    var isLayerSlide = slide && slide.classList.contains('parans-banner__slide--layers');
    if (isLayerSlide) {
      this.root.classList.add('is-slide-0');
      if (this.origBg) {
        this.root.style.setProperty('--parans-banner-bg', this.origBg);
      }
      if (this.origBgImg) {
        this.root.style.setProperty('--parans-banner-bg-img', this.origBgImg);
      }
    } else {
      this.root.classList.remove('is-slide-0');
      if (bg) this.root.style.setProperty('--parans-banner-bg', bg);
      this.root.style.setProperty('--parans-banner-bg-img', 'none');
    }
  };

  ParansBanner.prototype.activeFill = function () {
    var dot = this.dots[this.index];
    return dot ? dot.querySelector('.parans-banner__dot-fill') : null;
  };

  ParansBanner.prototype.clearFills = function () {
    this.dots.forEach(function (d) {
      var f = d.querySelector('.parans-banner__dot-fill');
      if (f) f.style.width = '0%';
    });
  };

  ParansBanner.prototype.restartProgress = function () {
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    this.elapsed = 0;
    this.clearFills();

    if (this.n <= 1) return;

    if (!this.autoplay) {
      // 不自动播放:当前屏指示条静态满格高亮
      var f = this.activeFill();
      if (f) f.style.width = '100%';
      return;
    }
    this.paused = false;
    this.lastTs = 0;
    var self = this;
    this.rafId = requestAnimationFrame(function (ts) {
      self.tick(ts);
    });
  };

  ParansBanner.prototype.tick = function (ts) {
    if (this.paused) return;
    if (!this.lastTs) this.lastTs = ts;
    var dt = ts - this.lastTs;
    this.lastTs = ts;
    this.elapsed += dt;

    var p = Math.min(1, this.elapsed / this.interval);
    var f = this.activeFill();
    if (f) f.style.width = p * 100 + '%';

    if (p >= 1) {
      this.go(this.index + 1, true);
      return;
    }
    var self = this;
    this.rafId = requestAnimationFrame(function (t) {
      self.tick(t);
    });
  };

  ParansBanner.prototype.pause = function () {
    this.paused = true;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
  };

  ParansBanner.prototype.resume = function () {
    if (!this.autoplay || this.n <= 1 || !this.paused) return;
    this.paused = false;
    this.lastTs = 0;
    var self = this;
    this.rafId = requestAnimationFrame(function (ts) {
      self.tick(ts);
    });
  };

  // ---- 拖拽 / 滑动 ----
  ParansBanner.prototype.onDown = function (e) {
    this.suppressClick = false;   // 新交互开始:清除上一次拖拽遗留的吞 click 标志(否则拖拽后第一次点按钮/遮罩会失效)
    if (this.n <= 1) return;
    if ((e.pointerType === 'mouse' || e.pointerId == null) && e.button !== 0) return;
    // 按在箭头/指示条等控件上时不进入拖拽,放行其原生 click(否则指针被 viewport 捕获,
    // click 被吞掉,只剩 onUp 的 go(当前屏) 把进度条重置而图片不切)
    if (e.target && e.target.closest &&
        e.target.closest('.parans-banner__arrow, .parans-banner__dot')) {
      return;
    }
    this.dragging = true;
    this.moved = false;
    this.startX = e.clientX;
    this.startY = e.clientY;
    this.deltaX = 0;
    this.baseShift = -this.index * this.vw();
    this.pointerId = e.pointerId == null ? 'mouse' : e.pointerId;
    this.dragAxis = '';
    this.track.classList.add('is-dragging');
    this.setTransform(this.baseShift, false);
    this.pause();
    this.captured = false;   // 先不捕获;等真正移动>5px 再捕获(否则纯点击的 click 被重定向到 viewport,到不了遮罩链接)
  };

  ParansBanner.prototype.onMove = function (e) {
    var eventPointerId = e.pointerId == null ? 'mouse' : e.pointerId;
    if (!this.dragging || eventPointerId !== this.pointerId) return;
    var visualX = e.clientX - this.startX;
    var visualY = e.clientY - this.startY;
    if (!this.dragAxis && Math.max(Math.abs(visualX), Math.abs(visualY)) > 5) {
      this.dragAxis = Math.abs(visualX) > Math.abs(visualY) ? 'x' : 'y';
    }
    if (this.dragAxis !== 'x') return;
    e.preventDefault();
    /* 0720:clientX 是视觉像素,zoom 环境下换算成布局像素,拖拽跟手 */
    this.deltaX = visualX / this.zoomFactor();
    if (Math.abs(this.deltaX) > 5) {
      this.moved = true;
      if (!this.captured && this.pointerId !== 'mouse') {
        try { this.viewport.setPointerCapture(this.pointerId); } catch (err) {}
        this.captured = true;
      }
    }
    var shift = this.baseShift + this.deltaX;
    // 非循环时,首/尾屏越界加阻尼
    if (!this.loop) {
      var atStart = this.index === 0 && this.deltaX > 0;
      var atEnd = this.index === this.n - 1 && this.deltaX < 0;
      if (atStart || atEnd) shift = this.baseShift + this.deltaX * 0.35;
    }
    this.setTransform(shift, false);
  };

  ParansBanner.prototype.onUp = function (e, cancelled) {
    var eventPointerId = e && e.pointerId == null ? 'mouse' : e && e.pointerId;
    if (!this.dragging || (e && eventPointerId !== this.pointerId)) return;
    this.dragging = false;
    this.track.classList.remove('is-dragging');
    if (this.moved) this.suppressClick = true;   // 只吞紧接着这次拖拽的合成 click
    if (this.captured) {
      try { this.viewport.releasePointerCapture(this.pointerId); } catch (err) {}
      this.captured = false;
    }
    /* 桌面横幅较宽，15% 需要拖近 300px，容易被误判为无效；改为 8%，上限 120 布局像素。 */
    var threshold = Math.min(120, Math.max(40, this.vw() * 0.08));
    if (cancelled) {
      this.go(this.index, true);
    } else if (this.deltaX <= -threshold) {
      this.go(this.index + 1, true);
    } else if (this.deltaX >= threshold) {
      this.go(this.index - 1, true);
    } else {
      this.go(this.index, true);
    }
    this.deltaX = 0;
    this.pointerId = null;
    this.dragAxis = '';
  };

  ParansBanner.prototype.bind = function () {
    var self = this;

    if (this.n > 1) {
      this.viewport.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse') self.onDown(e);
      });
      this.viewport.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse') self.onMove(e);
      });
      this.viewport.addEventListener('pointerup', function (e) {
        if (e.pointerType !== 'mouse') self.onUp(e, false);
      });
      this.viewport.addEventListener('pointercancel', function (e) {
        if (e.pointerType !== 'mouse') self.onUp(e, true);
      });
      /* 桌面端单独使用 Mouse Events，并把 move/up 监听到 window。
         这样即使整图链接触发浏览器原生拖动或指针离开 Banner，手势也不会中断。 */
      this.viewport.addEventListener('mousedown', function (e) {
        self.onDown(e);
      });
      window.addEventListener('mousemove', function (e) {
        self.onMove(e);
      });
      window.addEventListener('mouseup', function (e) {
        self.onUp(e, false);
      });
      this.viewport.addEventListener('dragstart', function (e) {
        e.preventDefault();
      });
      // 拖拽后吞掉误触 click(避免触发图层/整图链接)
      this.viewport.addEventListener(
        'click',
        function (e) {
          if (self.suppressClick) {
            e.preventDefault();
            e.stopPropagation();
            self.suppressClick = false;
          }
        },
        true
      );
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.go(self.index - 1, true);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.go(self.index + 1, true);
      });
    }

    this.dots.forEach(function (d, di) {
      d.addEventListener('click', function () {
        self.go(di, true);
      });
    });

    // 悬停暂停 / 离开续播
    this.root.addEventListener('mouseenter', function () {
      self.pause();
    });
    this.root.addEventListener('mouseleave', function () {
      self.resume();
    });

    // 标签页隐藏时暂停
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) self.pause();
      else self.resume();
    });

    // 窗口缩放:重新对齐当前屏(不动画)
    window.addEventListener('resize', function () {
      self.setTransform(-self.index * self.vw(), false);
    });

    // 主题编辑器:选中某屏区块时跳到该屏并暂停
    document.addEventListener('shopify:block:select', function (e) {
      if (!self.root.contains(e.target)) return;
      var slide = e.target.closest
        ? e.target.closest('.parans-banner__slide')
        : null;
      if (slide) {
        var idx = parseInt(slide.getAttribute('data-slide-index'), 10) || 0;
        self.go(idx, true);
        /* 0710方案D:Shopify 编辑器原生会把选中元素 scrollIntoView——对 transform 轮播
           它滚的是 viewport 的 scrollLeft,和我们的 translate 叠加后停在两屏交界,
           看起来像"一屏被切成两块"。原生滚动发生在本事件之后,延时清零对齐。 */
        var fixScroll = function () {
          if (self.viewport) self.viewport.scrollLeft = 0;
          self.setTransform(-self.index * self.vw(), false);
        };
        setTimeout(fixScroll, 50);
        setTimeout(fixScroll, 300);
        setTimeout(fixScroll, 700);
      }
      self.pause();
    });
    document.addEventListener('shopify:block:deselect', function (e) {
      if (!self.root.contains(e.target)) return;
      self.resume();
    });
  };

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-parans-carousel]');
    Array.prototype.forEach.call(nodes, function (el) {
      if (!el.__paransCarousel) new ParansBanner(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  // 主题编辑器动态加载 section
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
})();
